<template>
  <div class="edit-global-account">
    <div class="section-header">
      <div class="section-header__left">
        <div class="section-header__icon">
          <i class="el-icon-s-custom"></i>
        </div>
        <div>
          <h2 class="section-header__title">
            {{ account && account.appLoginName ? account.appLoginName : $t('settings.editAccount') }}
          </h2>
          <p class="section-header__subtitle">{{ $t('settings.editAccount') }}</p>
        </div>
      </div>

      <div class="section-header__actions">
        <base-button
          icon="el-icon-back"
          @click="$router.push({path: '/app/global/accounts'})">
          {{ $t('settings.backToAccounts') }}
        </base-button>
      </div>
    </div>

    <el-form
      v-loading="loading"
      class="account-form"
      ref="form"
      :rules="rules"
      :model="account"
      label-width="160px">

      <div class="form-card">
        <div class="form-card__header">
          <div class="form-card__icon">
            <i class="el-icon-user"></i>
          </div>
          <h3 class="form-card__title">{{ $t('settings.accountSection') }}</h3>
        </div>
        <div class="form-card__body">
          <el-form-item :label="$t('base.firstName')" prop="firstName">
            <el-input v-model="account.firstName"></el-input>
          </el-form-item>
          <el-form-item :label="$t('base.lastName')" prop="lastName">
            <el-input v-model="account.lastName"></el-input>
          </el-form-item>
          <el-form-item :label="$t('base.email')" prop="email">
            <el-input v-model="account.email"></el-input>
          </el-form-item>
          <el-form-item :label="$t('base.appLoginName')" prop="appLoginName">
            <el-input v-model="account.appLoginName"></el-input>
          </el-form-item>
        </div>
      </div>

      <div class="form-card">
        <div class="form-card__header">
          <div class="form-card__icon">
            <i class="el-icon-key"></i>
          </div>
          <h3 class="form-card__title">{{ $t('settings.accessSection') }}</h3>
        </div>
        <div class="form-card__body">
          <el-form-item :label="$t('settings.authority')">
            <el-select v-model="account.userAuthority" :placeholder="$t('settings.authority')">
              <el-option
                v-for="authority in authorities"
                :label="authority"
                :key="authority"
                :value="authority">
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('settings.active')">
            <el-switch v-model="account.active"></el-switch>
          </el-form-item>
          <el-form-item :label="$t('settings.mail_reception')">
            <el-switch v-model="account.receivesMail"></el-switch>
          </el-form-item>
        </div>
      </div>

      <div class="form-actions">
        <base-button @click="onCancel">
          {{ $t('base.cancel') }}
        </base-button>
        <base-button
          type="primary"
          :loading="saveLoading"
          @click="onSubmit">
          {{ $t('settings.save') }}
        </base-button>
      </div>
    </el-form>

    <div v-if="account" class="form-card">
      <div class="form-card__header">
        <div class="form-card__icon">
          <i class="el-icon-setting"></i>
        </div>
        <h3 class="form-card__title">{{ $t('settings.accountActions') }}</h3>
      </div>
      <div class="form-card__body form-card__body--actions">
        <div class="action-item">
          <div class="action-item__label">{{ $t('settings.account.sendEmail') }}</div>
          <base-button
            :disabled="!account.email"
            type="primary"
            plain
            icon="el-icon-message"
            :loading="emailLoading"
            @click="sendTestEmail">
            {{ $t('settings.account.sendTestEmail') }}
          </base-button>
        </div>

        <div class="action-item">
          <div class="action-item__label">{{ $t('settings.account.sendPush') }}</div>
          <base-button
            :disabled="!account.hasPushToken"
            type="primary"
            plain
            icon="el-icon-mobile-phone"
            :loading="pushLoading"
            @click="sendTestPush">
            {{ $t('settings.account.sendTestPush') }}
          </base-button>
        </div>

        <div class="action-item">
          <div class="action-item__label">{{ $t('settings.account.resetPushToken') }}</div>
          <base-button
            :disabled="!account.hasPushToken"
            type="warning"
            plain
            icon="el-icon-remove"
            :loading="tokenLoading"
            @click="resetPushToken">
            {{ $t('settings.account.resetAllTokens') }}
          </base-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/common/BaseButton';
import {adminAccountService} from '@/services/AdminAccountService.class';

export default {
  name: "EditGlobalAccount",
  components: {BaseButton},
  data() {
    return {
      account: {
        firstName: '',
        lastName: '',
        email: '',
        appLoginName: '',
        userAuthority: 'ADMIN',
        active: true,
        receivesMail: true,
        hasPushToken: false
      },
      loading: false,
      saveLoading: false,
      emailLoading: false,
      pushLoading: false,
      tokenLoading: false,
      authorities: ['SUPER_ADMIN', 'ADMIN', 'REPORTER', 'USER'],
      rules: {
        firstName: [
          {required: true, message: this.$t('settings.firstNameRequired'), trigger: 'blur'}
        ],
        lastName: [
          {required: true, message: this.$t('settings.lastNameRequired'), trigger: 'blur'}
        ],
        email: [
          {required: true, message: this.$t('settings.emailRequired'), trigger: 'blur'},
          {type: 'email', message: this.$t('settings.invalidEmail'), trigger: 'blur'}
        ],
        appLoginName: [
          {required: true, message: this.$t('settings.appLoginNameRequired'), trigger: 'blur'}
        ]
      }
    };
  },
  mounted() {
    this.loadAccount();
  },
  methods: {
    loadAccount() {
      this.loading = true;
      const accountId = this.$route.params.id;

      if (!accountId) {
        this.$message.error(this.$t('settings.accountNotFound'));
        this.$router.push({path: '/app/global/accounts'});
        return;
      }

      adminAccountService.getAccountById(accountId)
        .then(response => {
          this.account = response.data;
        })
        .catch(error => {
          console.error('Fehler beim Laden des Kontos:', error);
          this.$message.error(this.$t('settings.genericHttpError'));
          this.$router.push({path: '/app/global/accounts'});
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onSubmit() {
      this.$refs['form'].validate((valid) => {
        if (valid) {
          this.saveLoading = true;

          const updateActiveStatus = this.account.active !== this.originalActiveStatus;

          adminAccountService.updateAccount(this.account.id, this.account)
            .then(() => {
              this.$message.success(this.$t('settings.accountUpdated'));

              if (updateActiveStatus) {
                return adminAccountService.setAccountActive(this.account.id, this.account.active);
              }
            })
            .then(() => {
              if (updateActiveStatus) {
                this.$message.success(
                  this.account.active
                    ? this.$t('settings.account.activated')
                    : this.$t('settings.account.deactivated')
                );
              }
              this.loadAccount();
            })
            .catch(error => {
              console.error('Fehler beim Aktualisieren des Kontos:', error);
              this.$message.error(this.$t('settings.genericHttpError'));
            })
            .finally(() => {
              this.saveLoading = false;
            });
        }
      });
    },
    onCancel() {
      this.$router.push({path: '/app/global/accounts'});
    },
    sendTestEmail() {
      this.emailLoading = true;
      adminAccountService.sendTestEmail(this.account.id)
        .then(() => {
          this.$message.success(this.$t('settings.testEmailSent'));
        })
        .catch(error => {
          console.error('Fehler beim Senden der Test-E-Mail:', error);
          this.$message.error(this.$t('settings.testEmailFailed'));
        })
        .finally(() => {
          this.emailLoading = false;
        });
    },
    sendTestPush() {
      this.pushLoading = true;
      adminAccountService.sendTestPush(this.account.id)
        .then(() => {
          this.$message.success(this.$t('settings.testPushSent'));
        })
        .catch(error => {
          console.error('Fehler beim Senden der Test-Push-Benachrichtigung:', error);
          this.$message.error(this.$t('settings.testPushFailed'));
        })
        .finally(() => {
          this.pushLoading = false;
        });
    },
    resetPushToken() {
      this.tokenLoading = true;
      adminAccountService.deleteAllDeviceTokens(this.account.id)
        .then(() => {
          this.$message.success(this.$t('settings.pushTokensReset'));
          this.loadAccount();
        })
        .catch(error => {
          console.error('Fehler beim Zurücksetzen der Push-Tokens:', error);
          this.$message.error(this.$t('settings.pushTokensResetFailed'));
        })
        .finally(() => {
          this.tokenLoading = false;
        });
    }
  }
}
</script>

<style scoped lang="scss">
.edit-global-account {
  padding: 0 15px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  &__left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  &__icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      color: var(--bg-surface);
      font-size: 18px;
    }
  }

  &__title {
    margin: 0 0 2px;
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.3;
  }

  &__subtitle {
    margin: 0;
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1.4;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.account-form {
  width: 100%;
}

.form-card {
  background: var(--bg-surface);
  border-radius: 12px;
  border: 1px solid var(--border);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  margin-bottom: 20px;

  &__header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border);
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 9px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      color: var(--bg-surface);
      font-size: 16px;
    }
  }

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.3;
  }

  &__body {
    padding: 24px 20px 4px;

    .el-select {
      width: 100%;
    }

    &--actions {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      padding: 20px;
    }
  }
}

.action-item {
  flex: 1 1 220px;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__label {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .el-button {
    width: 100%;
    margin: 0;
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 20px;
}

@media (max-width: 767px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }

  .form-card__body {
    padding: 20px 14px 4px;
  }
}
</style>
